<div class="pp-root">

  <!-- ══ HEADER ══ -->
  <header class="pp-header">
    <div class="pp-header__inner">
      <div class="pp-logo-row">
        <div class="pp-logo-icon">🔧</div>
        <span class="pp-logo-text">AutoGizmo</span>
      </div>
      <h1 class="pp-title">Data Privacy Policy</h1>
      <p class="pp-subtitle">
        For Drivers, Customers &amp; Service Providers &nbsp;·&nbsp;
        Last Updated: May 2026 &nbsp;·&nbsp; Version 2.0
      </p>
      <div class="pp-badge-row">
        <span class="pp-badge"><span class="pp-badge__dot"></span>Google Play Compliant</span>
        <span class="pp-badge"><span class="pp-badge__dot"></span>Kenya DPA 2019</span>
        <span class="pp-badge"><span class="pp-badge__dot"></span>HTTPS Encrypted</span>
        <span class="pp-badge"><span class="pp-badge__dot"></span>ODPC Registered</span>
      </div>
    </div>
  </header>

  <!-- ══ BODY LAYOUT ══ -->
  <div class="pp-layout">

    <!-- TOC -->
    <nav class="pp-toc" aria-label="Table of contents">
      <p class="pp-toc__title">Contents</p>
      <a
        *ngFor="let item of tocItems"
        class="pp-toc__link"
        [class.pp-toc__link--active]="activeSectionId === item.id"
        (click)="scrollTo(item.id)"
      >{{ item.label }}</a>
    </nav>

    <!-- MAIN CONTENT -->
    <main class="pp-content">

      <!-- ── PROMINENT DISCLOSURE ── -->
      <div class="pp-disclosure" id="disclosure">
        <p class="pp-disclosure__label">■ Prominent Data Disclosure — Required by Google Play</p>
        <p>
          AutoGizmo collects location data, device identifiers, contact information, vehicle data,
          and payment details to enable automotive service matching, real-time tracking, billing,
          identity verification, fraud prevention, and service quality assurance — including when
          the app is running in the background during an active service request.
        </p>
        <p class="pp-disclosure__note">
          This disclosure is provided in accordance with Google Play's Prominent Disclosure &amp;
          Consent Policy. By using AutoGizmo you consent to the data practices described in this
          policy. You may withdraw consent at any time through
          <strong>Settings → Privacy Center</strong>.
        </p>
      </div>

      <!-- ── SECTION 1 ── -->
      <section class="pp-section" id="s1">
        <div class="pp-section__header">
          <span class="pp-section__num">01</span>
          <h2 class="pp-section__title">Introduction &amp; Scope</h2>
        </div>

        <h3 class="pp-h3">1.1 Who This Policy Covers</h3>
        <p>This Privacy Policy applies to all users of the AutoGizmo mobile application and website when requesting or providing automotive services in Kenya, including:</p>
        <ul class="pp-list">
          <li><strong>Customers / Drivers</strong> — individuals requesting battery, towing, tyre, fuel, mechanical, maintenance, or car-wash services</li>
          <li><strong>Service Providers</strong> — professionals and companies fulfilling service requests through the platform</li>
          <li><strong>Guest Users</strong> — individuals accessing limited features without a registered account</li>
        </ul>

        <h3 class="pp-h3">1.2 Legal Framework</h3>
        <p>Galweather Consulting Limited (trading as AutoGizmo) is registered with Kenya's Office of the Data Protection Commissioner (ODPC) and operates in full compliance with:</p>
        <ul class="pp-list">
          <li>Kenya Data Protection Act, 2019 (DPA)</li>
          <li>Google Play Developer Program Policies — User Data section</li>
          <li>Central Bank of Kenya regulations (mobile money / payment data)</li>
          <li>Kenya Revenue Authority reporting requirements</li>
        </ul>
      </section>

      <!-- ── SECTION 2 ── -->
      <section class="pp-section" id="s2">
        <div class="pp-section__header">
          <span class="pp-section__num">02</span>
          <h2 class="pp-section__title">Data We Collect</h2>
        </div>

        <h3 class="pp-h3">2.1 Data You Provide Directly</h3>

        <p><strong>Account Information</strong></p>
        <ul class="pp-list">
          <li>Full name, email address, and phone number</li>
          <li>Encrypted login credentials (password is never stored in plain text)</li>
          <li>Profile photo</li>
          <li>Payment method details (tokenised; raw card numbers never stored)</li>
          <li>Settings and preferences</li>
        </ul>

        <p><strong>Driver / Customer Information</strong></p>
        <ul class="pp-list">
          <li>Vehicle information: make, model, year, VIN, licence plate</li>
          <li>Vehicle service history and preferences</li>
          <li>Emergency contact details (optional, with separate consent)</li>
          <li>Service location history</li>
        </ul>

        <p><strong>Service Provider Information</strong></p>
        <ul class="pp-list">
          <li>Business registration details, company name and address</li>
          <li>Tax PIN (KRA) and relevant business licences</li>
          <li>Professional certifications (e.g. battery specialist, towing certified)</li>
          <li>Service area, coverage zones, and equipment information</li>
          <li>Bank account or mobile-money details for payment disbursement</li>
        </ul>

        <p><strong>Identity Verification</strong> <em>(collected with explicit consent)</em></p>
        <ul class="pp-list">
          <li>National ID / Passport number</li>
          <li>Driving licence information</li>
          <li>KRA PIN certificate</li>
          <li>Business registration certificates</li>
          <li>Verification selfies / photos</li>
        </ul>

        <div class="pp-callout">
          <strong>Note:</strong> Government ID numbers are never publicly disclosed. They are used solely for identity verification and are stored in encrypted form.
        </div>

        <p><strong>Service-Related Content</strong></p>
        <ul class="pp-list">
          <li>Service request details, photos/videos of vehicles and issues</li>
          <li>Diagnostic reports and repair / service records</li>
          <li>Service estimates, invoices, and ratings</li>
          <li>In-app chat and communication logs</li>
        </ul>

        <h3 class="pp-h3">2.2 Data Collected Automatically</h3>

        <p><strong>Location Data</strong><br>
          AutoGizmo collects precise GPS location during active service requests and, for Service Providers, during active assignments. Location collection in the background occurs only while a service is in progress.
        </p>

        <p><strong>Device Data</strong></p>
        <ul class="pp-list">
          <li>Device type, model, and operating system version</li>
          <li>Unique device identifiers (fraud prevention only; not linked to ad profiles)</li>
          <li>Mobile network information and IP address</li>
          <li>App Set ID — used for analytics and fraud prevention only; never used for ads personalisation</li>
        </ul>

        <p><strong>Usage Data</strong></p>
        <ul class="pp-list">
          <li>App features accessed and session information</li>
          <li>Search queries within the app</li>
          <li>App performance and crash data</li>
          <li>Service history and interaction patterns</li>
        </ul>

        <p><strong>Communications Data</strong></p>
        <ul class="pp-list">
          <li>In-app messages and chat logs</li>
          <li>Call metadata if using in-app calling features (not call content)</li>
          <li>Customer support communications</li>
          <li>Push notification interactions</li>
        </ul>

        <h3 class="pp-h3">2.3 Data From Third Parties</h3>
        <ul class="pp-list">
          <li>Background check and professional verification services</li>
          <li>Payment processors (M-Pesa, Airtel Money, banks) — transaction references and amounts only</li>
          <li>Insurance companies (for claim-related purposes)</li>
          <li>Vehicle service history databases</li>
          <li>Referral partners (with your prior consent)</li>
        </ul>

        <h3 class="pp-h3">2.4 Data We Do NOT Collect</h3>
        <ul class="pp-list">
          <li>Mobile money PINs or passwords</li>
          <li>Raw payment card numbers</li>
          <li>Content of phone calls</li>
          <li>Data from your device's phonebook or contacts (unless explicitly granted)</li>
          <li>Health or biometric data beyond identity verification photos</li>
          <li>Inventory of other installed apps</li>
        </ul>
      </section>

      <!-- ── SECTION 3 ── -->
      <section class="pp-section" id="s3">
        <div class="pp-section__header">
          <span class="pp-section__num">03</span>
          <h2 class="pp-section__title">How We Use Your Data</h2>
        </div>

        <div class="pp-table-wrap">
          <table class="pp-table">
            <thead>
              <tr><th>Purpose</th><th>Legal Basis</th><th>Data Types Used</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of purposes">
                <td>{{ row.purpose }}</td>
                <td>{{ row.basis }}</td>
                <td>{{ row.data }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="pp-h3">3.1 Automated Decision-Making</h3>
        <p>AutoGizmo uses automated algorithms for service matching (based on proximity, specialisation, and ratings), dynamic pricing, and quality assurance monitoring. You have the right to request human review of any automated decision that significantly affects you.</p>

        <h3 class="pp-h3">3.2 Advertising</h3>
        <p>AutoGizmo does not use personal and sensitive user data for personalised advertising. If in-app promotional content is displayed, it is based solely on service type and general usage context — never on sensitive identifiers.</p>
      </section>

      <!-- ── SECTION 4 ── -->
      <section class="pp-section" id="s4">
        <div class="pp-section__header">
          <span class="pp-section__num">04</span>
          <h2 class="pp-section__title">Data Sharing &amp; Disclosure</h2>
        </div>

        <h3 class="pp-h3">4.1 With Other Users (Service Delivery)</h3>
        <ul class="pp-list">
          <li><strong>Shared with your assigned Service Provider:</strong> first name, approximate pickup location (full address only once service begins), vehicle details relevant to the service, and service requirements</li>
          <li><strong>Shared with Customers:</strong> provider's name, company, relevant certifications, ratings, contact number, and real-time location during the service</li>
        </ul>

        <h3 class="pp-h3">4.2 With Service Partners (Data Processors)</h3>
        <ul class="pp-list">
          <li>Payment processors — transaction references and amounts only; tokenisation used</li>
          <li>Cloud infrastructure providers (data stored primarily in Kenya)</li>
          <li>SMS and push notification services</li>
          <li>Analytics and customer support platforms (anonymised / pseudonymised data)</li>
          <li>Background verification services</li>
        </ul>
        <div class="pp-callout">All service partners are bound by data processing agreements requiring them to protect your data to the same standard as AutoGizmo.</div>

        <h3 class="pp-h3">4.3 For Legal &amp; Regulatory Purposes</h3>
        <ul class="pp-list">
          <li>Kenyan authorities pursuant to a valid legal requirement</li>
          <li>Kenya Revenue Authority (KRA) for tax compliance</li>
          <li>Law enforcement for lawful investigations</li>
          <li>Insurance companies for service-related claim processing</li>
          <li>Successor entities in the event of a merger or acquisition (with legally adequate notice)</li>
        </ul>

        <h3 class="pp-h3">4.4 With Your Explicit Consent</h3>
        <ul class="pp-list">
          <li>Third-party services you authorise within the app</li>
          <li>Marketing partnerships (opt-in required; you may withdraw consent at any time)</li>
        </ul>

        <h3 class="pp-h3">4.5 Data We Do NOT Sell</h3>
        <div class="pp-callout pp-callout--strong">
          <strong>AutoGizmo does not sell personal and sensitive user data to any third party for monetary consideration.</strong>
          This applies to all data categories including location, device identifiers, financial information, and behavioural data.
        </div>
      </section>

      <!-- ── SECTION 5 ── -->
      <section class="pp-section" id="s5">
        <div class="pp-section__header">
          <span class="pp-section__num">05</span>
          <h2 class="pp-section__title">Consent, Permissions &amp; User Controls</h2>
        </div>

        <h3 class="pp-h3">5.1 Runtime Permission Requests</h3>
        <div class="pp-table-wrap">
          <table class="pp-table">
            <thead>
              <tr><th>Permission</th><th>When Requested</th><th>Purpose</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of permissions">
                <td>{{ p.name }}</td>
                <td>{{ p.when }}</td>
                <td>{{ p.purpose }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>Consent dialogs clearly describe the purpose of each permission. Navigating away is not treated as acceptance. You may revoke any permission at any time via device settings.</p>

        <h3 class="pp-h3">5.2 Privacy Settings &amp; Controls</h3>
        <p>Access privacy controls at: <strong>Account → Settings → Privacy Center</strong></p>
        <ul class="pp-list">
          <li>Location sharing (real-time vs approximate)</li>
          <li>Service history visibility</li>
          <li>Profile information display to other users</li>
          <li>Communication preferences (SMS, push, email)</li>
          <li>Marketing and promotional consents</li>
          <li>Emergency contact sharing preferences</li>
          <li>Service photo storage preferences</li>
          <li>Rating display settings (Service Providers)</li>
        </ul>

        <h3 class="pp-h3">5.3 Marketing Preferences</h3>
        <p>Marketing communications are sent only with your explicit opt-in consent. You may withdraw consent at any time through account settings, by clicking 'Unsubscribe' in any marketing message, or by contacting <strong>support&#64;autogizmo.com</strong>.</p>
      </section>

      <!-- ── SECTION 6 ── -->
      <section class="pp-section" id="s6">
        <div class="pp-section__header">
          <span class="pp-section__num">06</span>
          <h2 class="pp-section__title">Data Retention &amp; Deletion</h2>
        </div>

        <div class="pp-table-wrap">
          <table class="pp-table">
            <thead>
              <tr><th>Data Category</th><th>Retention Period</th><th>Rationale</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of retentionRows">
                <td>{{ r.category }}</td>
                <td>{{ r.period }}</td>
                <td>{{ r.rationale }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 class="pp-h3">6.1 Account Deletion</h3>
        <p>You can request full account deletion at any time via <strong>Settings → Account → Delete Account</strong>. Upon deletion, all personal data is erased within 30 days except data we are legally required to retain. Temporary deactivation does not constitute deletion.</p>
      </section>

      <!-- ── SECTION 7 ── -->
      <section class="pp-section" id="s7">
        <div class="pp-section__header">
          <span class="pp-section__num">07</span>
          <h2 class="pp-section__title">Data Security</h2>
        </div>
        <ul class="pp-list">
          <li><strong>Encryption in transit:</strong> all data transmitted uses TLS 1.2+ (HTTPS)</li>
          <li><strong>Encryption at rest:</strong> sensitive data fields (IDs, payment tokens, credentials) are encrypted in the database</li>
          <li><strong>Access controls:</strong> role-based access limiting staff access on a need-to-know basis</li>
          <li><strong>Regular security audits</strong> and penetration testing</li>
          <li><strong>Data Protection Impact Assessments (DPIAs)</strong> for high-risk processing activities</li>
          <li><strong>Employee training</strong> on data protection and security</li>
          <li><strong>Breach notification:</strong> affected users and the ODPC notified within 72 hours of discovering a breach</li>
        </ul>
      </section>

      <!-- ── SECTION 8 ── -->
      <section class="pp-section" id="s8">
        <div class="pp-section__header">
          <span class="pp-section__num">08</span>
          <h2 class="pp-section__title">Third-Party SDKs &amp; Integrated Services</h2>
        </div>

        <div class="pp-table-wrap">
          <table class="pp-table">
            <thead>
              <tr><th>SDK / Service</th><th>Purpose</th><th>Data Collected</th><th>Privacy Policy</th></tr>
            </thead>
            <tbody>
              <tr *ngFor="let s of sdkRows">
                <td>{{ s.name }}</td>
                <td>{{ s.purpose }}</td>
                <td>{{ s.data }}</td>
                <td class="pp-table__mono">{{ s.policy }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>If we integrate additional third-party SDKs that collect personal or sensitive data by default, we will update this policy and provide prominent in-app disclosure within 2 weeks of integration.</p>
      </section>

      <!-- ── SECTION 9 ── -->
      <section class="pp-section" id="s9">
        <div class="pp-section__header">
          <span class="pp-section__num">09</span>
          <h2 class="pp-section__title">Your Rights Under Kenyan Law</h2>
        </div>
        <p>Under Kenya's Data Protection Act, 2019 (Articles 26–34), you have the following rights:</p>
        <div class="pp-rights-grid">
          <div class="pp-right-card" *ngFor="let r of rights">
            <p class="pp-right-card__title">{{ r.title }}</p>
            <p class="pp-right-card__desc">{{ r.description }}</p>
            <p class="pp-right-card__how">{{ r.how }}</p>
          </div>
        </div>
      </section>

      <!-- ── SECTION 10 ── -->
      <section class="pp-section" id="s10">
        <div class="pp-section__header">
          <span class="pp-section__num">10</span>
          <h2 class="pp-section__title">Legal Bases for Processing</h2>
        </div>
        <ul class="pp-list">
          <li><strong>Consent</strong> — marketing communications, background location access, emergency contact sharing, special category data</li>
          <li><strong>Contractual Necessity</strong> — service provision, matching, payment, communications essential to delivery</li>
          <li><strong>Legal Obligation</strong> — tax reporting, KYC/AML compliance, law enforcement cooperation</li>
          <li><strong>Legitimate Interests</strong> — fraud prevention, service quality improvement, security, business analytics (balanced against your rights)</li>
          <li><strong>Vital Interests</strong> — emergency coordination where life is at risk</li>
        </ul>
      </section>

      <!-- ── SECTION 11 ── -->
      <section class="pp-section" id="s11">
        <div class="pp-section__header">
          <span class="pp-section__num">11</span>
          <h2 class="pp-section__title">Cross-Border Data Transfers</h2>
        </div>
        <p>Your data is primarily stored and processed in Kenya. Where data is transferred outside Kenya, AutoGizmo ensures:</p>
        <ul class="pp-list">
          <li>Transfers occur only to countries with adequate data protection as recognised by ODPC, or</li>
          <li>Appropriate safeguards are in place (standard contractual clauses, binding corporate rules), and</li>
          <li>You are informed and, where required, explicit consent is obtained</li>
        </ul>
      </section>

      <!-- ── SECTION 12 ── -->
      <section class="pp-section" id="s12">
        <div class="pp-section__header">
          <span class="pp-section__num">12</span>
          <h2 class="pp-section__title">Children's Privacy</h2>
        </div>
        <p>AutoGizmo services are not directed at persons under 18 years of age. We do not knowingly collect personal data from minors. If you believe a minor has provided data to us, please contact <strong>dpo&#64;autogizmo.com</strong> immediately and we will delete such data without delay.</p>
      </section>

      <!-- ── SECTION 13 ── -->
      <section class="pp-section" id="s13">
        <div class="pp-section__header">
          <span class="pp-section__num">13</span>
          <h2 class="pp-section__title">Cookies &amp; Similar Technologies</h2>
        </div>
        <p>AutoGizmo uses cookies and similar technologies for authentication, preference storage, analytics, and (with consent) marketing. A full Cookie Policy is accessible within the app under <strong>Settings → Privacy Center → Cookie Policy</strong>. You may manage cookie preferences at any time through the in-app Cookie Preference Centre.</p>
      </section>

      <!-- ── SECTION 14 ── -->
      <section class="pp-section" id="s14">
        <div class="pp-section__header">
          <span class="pp-section__num">14</span>
          <h2 class="pp-section__title">App Set ID Usage</h2>
        </div>
        <ul class="pp-list">
          <li>App Set ID is not used for ads personalisation or ads measurement</li>
          <li>App Set ID is not linked to the Android Advertising ID (AAID) or any personal and sensitive data for advertising purposes</li>
          <li>Usage is limited to internal analytics and detecting fraudulent service requests</li>
        </ul>
      </section>

      <!-- ── SECTION 15 ── -->
      <section class="pp-section" id="s15">
        <div class="pp-section__header">
          <span class="pp-section__num">15</span>
          <h2 class="pp-section__title">Updates to This Policy</h2>
        </div>
        <p>We may update this policy to reflect service changes, legal requirements, or improved privacy practices. For significant changes, we will provide notice via:</p>
        <ul class="pp-list">
          <li>In-app notification banners (displayed before the change takes effect)</li>
          <li>Email communication to your registered address</li>
          <li>SMS alerts for critical changes affecting your rights</li>
          <li>Updated 'Last Updated' date at the top of this document</li>
        </ul>
        <p>Where required by law, we will seek your renewed consent before applying material changes.</p>
      </section>

      <!-- ── SECTION 16 ── -->
      <section class="pp-section" id="s16">
        <div class="pp-section__header">
          <span class="pp-section__num">16</span>
          <h2 class="pp-section__title">Contact Us &amp; Complaints</h2>
        </div>
        <div class="pp-contact-grid">
          <div class="pp-contact-card">
            <p class="pp-contact-card__role">Customer Support</p>
            <p class="pp-contact-card__name">AutoGizmo Support</p>
            <p class="pp-contact-card__detail">support&#64;autogizmo.com</p>
          </div>
          <div class="pp-contact-card">
            <p class="pp-contact-card__role">Service Provider Relations</p>
            <p class="pp-contact-card__name">Partner Relations</p>
            <p class="pp-contact-card__detail">partners&#64;autogizmo.com</p>
          </div>
          <div class="pp-contact-card">
            <p class="pp-contact-card__role">Data Protection Officer</p>
            <p class="pp-contact-card__name">Galweather Consulting Ltd</p>
            <p class="pp-contact-card__detail">dpo&#64;autogizmo.com</p>
            <p class="pp-contact-card__detail">george&#64;galweatherconsult.co.ke</p>
            <p class="pp-contact-card__detail">+254 720 730 814</p>
          </div>
          <div class="pp-contact-card">
            <p class="pp-contact-card__role">Regulatory Authority (ODPC)</p>
            <p class="pp-contact-card__name">Office of the Data Protection Commissioner</p>
            <p class="pp-contact-card__detail">info&#64;odpc.go.ke</p>
            <p class="pp-contact-card__detail">0709 983 000</p>
            <p class="pp-contact-card__detail pp-contact-card__detail--muted">CA Centre, Waiyaki Way, P.O. Box 30920-00100, Nairobi</p>
          </div>
        </div>
        <p class="pp-mt">If your complaint is not resolved within 30 days of contacting our Data Protection Officer, you may lodge a formal complaint with the ODPC.</p>
      </section>

      <!-- ── SECTION 17 ── -->
      <section class="pp-section" id="s17">
        <div class="pp-section__header">
          <span class="pp-section__num">17</span>
          <h2 class="pp-section__title">Definitions</h2>
        </div>
        <dl class="pp-dl">
          <ng-container *ngFor="let d of definitions">
            <dt class="pp-dl__term">{{ d.term }}</dt>
            <dd class="pp-dl__def">{{ d.def }}</dd>
          </ng-container>
        </dl>
      </section>

    </main>
  </div>

  <!-- ══ FOOTER ══ -->
  <footer class="pp-footer">
    <p>Galweather Consulting Limited (trading as AutoGizmo) · Registered under the laws of Kenya · ODPC Registered</p>
    <p>Plot 209 ICD Road, Nairobi, Kenya · george&#64;galweatherconsult.co.ke · www.autogizmo.com</p>
    <p class="pp-footer__copy">© 2026 AutoGizmo. All rights reserved. · Review annually or when services materially change.</p>
  </footer>

</div>
